import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./Calendar.module.css";
import {
  MONTH_LABELS,
  WEEKDAY_LABELS_LONG,
  WEEKDAY_LABELS_SHORT,
  buildMonthGrid,
  monthRangeForAvailabilityFetch,
  shiftMonth,
} from "./monthGrid";
import { fetchAvailability } from "../../utils/apiClient";
import {
  addDaysToDateString,
  formatDateLong,
  todayInTimeZone,
  weekdayMondayFirst,
} from "../../utils/time";

interface CalendarProps {
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
}

const TIMEZONE = "Europe/Amsterdam";

export function Calendar({ selectedDate, onSelectDate }: CalendarProps) {
  const today = useMemo(() => todayInTimeZone(TIMEZONE), []);
  const [{ year, month0 }, setDisplayedMonth] = useState(() => {
    const [y, m] = today.split("-").map(Number);
    return { year: y!, month0: m! - 1 };
  });

  const [availability, setAvailability] = useState<Map<string, boolean>>(new Map());
  const [bounds, setBounds] = useState<{ earliestDate: string; latestDate: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [focusedDate, setFocusedDate] = useState<string>(selectedDate ?? today);

  const gridRef = useRef<HTMLTableElement>(null);

  const loadAvailability = useCallback(async (y: number, m0: number) => {
    setLoading(true);
    setError(null);
    try {
      const { from, to } = monthRangeForAvailabilityFetch(y, m0);
      const response = await fetchAvailability(from, to);
      const map = new Map<string, boolean>();
      for (const day of response.days) {
        map.set(day.date, day.available);
      }
      setAvailability(map);
      setBounds({ earliestDate: response.meta.earliestDate, latestDate: response.meta.latestDate });
    } catch {
      setError(
        "Het lukt op dit moment niet om de beschikbaarheid op te halen. Probeer het opnieuw.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAvailability(year, month0);
  }, [year, month0, loadAvailability]);

  const weeks = useMemo(() => buildMonthGrid(year, month0), [year, month0]);

  const isDateDisabled = useCallback(
    (date: string): boolean => {
      // Zolang de beschikbaarheid nog niet is geladen, behandelen we alles
      // als niet-beschikbaar - anders zou een datum kort als beschikbaar
      // kunnen opflitsen voordat de echte server-bevestigde grenzen bekend zijn.
      if (!bounds) return true;
      if (date < today) return true;
      if (date < bounds.earliestDate || date > bounds.latestDate) return true;
      const available = availability.get(date);
      return available === false;
    },
    [availability, bounds, today],
  );

  // Zodra de beschikbaarheid bekend is: als het huidige focusdoel een
  // uitgeschakelde dag blijkt (bijv. "vandaag" valt binnen de minimale
  // voorbereidingstijd), verplaats de roving tabindex naar de eerstvolgende
  // beschikbare dag in het zichtbare rooster. Zo landt de toetsenbordfocus
  // nooit op een knop die niet gefocust kan worden.
  useEffect(() => {
    if (!bounds) return;
    if (!isDateDisabled(focusedDate)) return;
    const firstAvailable = weeks.flat().find((day) => !isDateDisabled(day.date));
    if (firstAvailable) {
      setFocusedDate(firstAvailable.date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bounds, availability, weeks]);

  function goToMonth(y: number, m0: number) {
    setDisplayedMonth({ year: y, month0: m0 });
  }

  function handlePrevMonth() {
    const { year: y, month0: m } = shiftMonth(year, month0, -1);
    goToMonth(y, m);
  }

  function handleNextMonth() {
    const { year: y, month0: m } = shiftMonth(year, month0, 1);
    goToMonth(y, m);
  }

  function selectDate(date: string) {
    if (isDateDisabled(date)) return;
    onSelectDate(date);
    setFocusedDate(date);
  }

  function moveFocus(delta: number) {
    const next = addDaysToDateString(focusedDate, delta);
    const [ny, nm] = next.split("-").map(Number);
    if (ny !== year || nm! - 1 !== month0) {
      goToMonth(ny!, nm! - 1);
    }
    setFocusedDate(next);
  }

  function handleGridKeyDown(event: React.KeyboardEvent<HTMLTableElement>) {
    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        moveFocus(-1);
        break;
      case "ArrowRight":
        event.preventDefault();
        moveFocus(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        moveFocus(-7);
        break;
      case "ArrowDown":
        event.preventDefault();
        moveFocus(7);
        break;
      case "PageUp": {
        event.preventDefault();
        const { year: y, month0: m } = shiftMonth(year, month0, event.shiftKey ? -12 : -1);
        goToMonth(y, m);
        break;
      }
      case "PageDown": {
        event.preventDefault();
        const { year: y, month0: m } = shiftMonth(year, month0, event.shiftKey ? 12 : 1);
        goToMonth(y, m);
        break;
      }
      case "Home":
        event.preventDefault();
        moveFocus(-weekdayMondayFirst(focusedDate));
        break;
      case "End":
        event.preventDefault();
        moveFocus(6 - weekdayMondayFirst(focusedDate));
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        selectDate(focusedDate);
        break;
      default:
        break;
    }
  }

  // Focus het roostercel dat logisch de focus heeft wanneer de maand wisselt.
  useEffect(() => {
    const button = gridRef.current?.querySelector<HTMLButtonElement>(
      `[data-date="${focusedDate}"]`,
    );
    if (
      button &&
      document.activeElement !== button &&
      gridRef.current?.contains(document.activeElement)
    ) {
      button.focus();
    }
  }, [focusedDate, year, month0]);

  const monthLabel = `${MONTH_LABELS[month0]} ${year}`;

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.navButton}
          onClick={handlePrevMonth}
          aria-label="Vorige maand"
        >
          ‹
        </button>
        <h3 className={styles.monthLabel} aria-live="polite">
          {monthLabel}
        </h3>
        <button
          type="button"
          className={styles.navButton}
          onClick={handleNextMonth}
          aria-label="Volgende maand"
        >
          ›
        </button>
      </div>

      {error && (
        <p role="alert" className={styles.errorBanner}>
          {error}
        </p>
      )}

      <table
        ref={gridRef}
        className={styles.grid}
        onKeyDown={handleGridKeyDown}
        aria-label={`Kalender, ${monthLabel}`}
      >
        <thead>
          <tr>
            {WEEKDAY_LABELS_SHORT.map((label, index) => (
              <th key={label} scope="col" abbr={WEEKDAY_LABELS_LONG[index]}>
                <span aria-hidden="true">{label}</span>
                <span className="dmm-visually-hidden">{WEEKDAY_LABELS_LONG[index]}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week[0]!.date}>
              {week.map((day) => {
                const disabled = isDateDisabled(day.date);
                const isSelected = day.date === selectedDate;
                const isFocusTarget = day.date === focusedDate;
                const label = `${formatDateLong(day.date)}${
                  disabled ? ", niet beschikbaar" : isSelected ? ", geselecteerd" : ", beschikbaar"
                }`;

                return (
                  <td
                    key={day.date}
                    className={day.isCurrentMonth ? undefined : styles.outsideMonth}
                  >
                    <button
                      type="button"
                      data-date={day.date}
                      className={[
                        styles.dayButton,
                        isSelected ? styles.daySelected : "",
                        disabled ? styles.dayDisabled : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      disabled={disabled}
                      tabIndex={isFocusTarget ? 0 : -1}
                      aria-pressed={isSelected}
                      aria-label={label}
                      onClick={() => selectDate(day.date)}
                      onFocus={() => setFocusedDate(day.date)}
                    >
                      {day.dayOfMonth}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {loading && (
        <p className={styles.loadingText} aria-live="polite">
          Beschikbaarheid wordt geladen...
        </p>
      )}

      <ul className={styles.legend} aria-hidden="true">
        <li>
          <span className={[styles.legendSwatch, styles.legendAvailable].join(" ")} /> Beschikbaar
        </li>
        <li>
          <span className={[styles.legendSwatch, styles.legendSelected].join(" ")} /> Geselecteerd
        </li>
        <li>
          <span className={[styles.legendSwatch, styles.legendUnavailable].join(" ")} /> Niet
          beschikbaar
        </li>
      </ul>
    </div>
  );
}
